"use client";

import { ArrowRight, Users, Repeat, Car, BookOpen, BrainCircuit, FileText } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Services() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: ctaRef,
    isVisible: ctaVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const services = [{
    number: "01",
    title: "Supervised Parenting Time",
    description: "Facilitating safe, neutral contact between children and parents within a strictly monitored framework to ensure physical and emotional safety.",
    Icon: Users
  }, {
    number: "02",
    title: "Monitored Exchange",
    description: "Professional oversight of child transitions between caregivers, maintaining a conflict-free environment and adhering to court-ordered protocols.",
    Icon: Repeat
  }, {
    number: "03",
    title: "Transportation Services",
    description: "Secure, supervised transit for children and vulnerable individuals, ensuring safety and compliance throughout the duration of the transfer.",
    Icon: Car
  }, {
    number: "04",
    title: "Parenting Education",
    description: "Structured, evidence-based instruction focused on developing child-centered communication, conflict resolution, and healthy boundary setting.",
    Icon: BookOpen
  }, {
    number: "05",
    title: "Therapeutic Supervision",
    description: "Clinical oversight for families requiring specialized support during reunification, guided by trauma-informed practices and professional intervention.",
    Icon: BrainCircuit
  }, {
    number: "06",
    title: "Observation Reporting",
    description: "Provision of objective, factual, and neutral documentation regarding parenting sessions to assist the court in legal and safety determinations.",
    Icon: FileText
  }];
  return <section data-section-id="241" id="serviceguidelines" className="bg-[#2F3631] text-white py-20 md:py-28 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-16 md:mb-20 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="font-serif italic text-base text-primary/70">02</span>
              <span className="h-px w-8 bg-primary/30" />
              <span className="font-sans uppercase tracking-widest text-xs text-primary/80">
                Our Services
              </span>
            </div>
            <h2 className="font-default font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-white">
              Professional support for{" "}
              <span className="italic font-light text-primary">Arizona families.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pt-4">
            <p className="text-base md:text-lg leading-relaxed text-white/80 max-w-md lg:ml-auto">
              Tiny to Teen Supervised Parenting & Education Services provides safe, structured, and neutral support for families navigating court involvement, separation, and reunification. We are committed to child-centered care.
            </p>
          </div>
        </div>

        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10">
          {services.map((service, i) => {
          const Icon = service.Icon;
          return <div key={i} data-index={i} className={`group relative bg-card p-8 md:p-10 border border-transparent hover:border-primary transition-all duration-500 ease-out hover:-translate-y-0.5 ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
            transitionDelay: gridVisible ? `${i * 80}ms` : "0ms"
          }}>
                <span className="absolute top-6 right-6 font-serif italic text-3xl text-foreground/20 group-hover:text-primary transition-colors duration-500">
                  {service.number}
                </span>

                <div className="mb-10">
                  <Icon className="w-10 h-10 text-primary" strokeWidth={1.25} aria-hidden="true" />
                </div>

                <h3 className="font-default font-serif text-2xl font-normal text-foreground mb-3 leading-snug">
                  {service.title}
                </h3>

                <p className="text-sm md:text-base leading-relaxed text-muted-foreground mb-8 max-w-sm">
                  {service.description}
                </p>

                <Link to="/OurServicesPage" className="inline-flex items-center gap-2 text-sm font-sans uppercase tracking-wider text-foreground hover:text-primary transition-colors duration-300">
                  <span>Learn more</span>
                  <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
                </Link>
              </div>;
        })}
        </div>

        <div ref={ctaRef} className={`mt-16 md:mt-20 text-center transition-all duration-700 ease-out ${ctaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <Button asChild variant="link" className="text-white hover:text-primary font-sans uppercase tracking-wider text-sm h-auto p-0">
            <Link to="/OurServicesPage" className="inline-flex items-center gap-2 group">
              <span className="border-b border-white/30 group-hover:border-primary pb-1 transition-colors duration-300">
                View all service details
              </span>
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}